import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import WellwiredInquiry from '../components/WellwiredInquiry';
import WellwiredDisclosure from '../components/WellwiredDisclosure';
import WellwiredClock from '../components/WellwiredClock';
import { RoomStudy, PillarStudy } from '../components/WellwiredVisuals';
import useWellwiredScroll from '../hooks/useWellwiredScroll';
import { blueprintRate, consultPrice, fitCallUrl } from '../config/wellwired';
import './WellwiredTokens.css';
import './Wellwired.css';
import './WellwiredNext.css';

const openings = {
  a: {
    eyebrow: 'Indoor air quality · Low-RF technology',
    first: 'Air quality first.',
    second: 'Hardwired by design.',
  },
  b: {
    eyebrow: 'Low-RF technology · Indoor air quality',
    first: 'Low-RF technology.',
    second: 'Air quality built in.',
  },
};

const dayScenes = [
  {
    name: 'Dawn', time: '06:30', minutes: 390,
    title: 'Morning light, ready when you are.',
    copy: 'The daytime lighting scene begins on schedule. Ordinary switches remain ready whenever you want something different.',
  },
  {
    name: 'Midday', time: '12:00', minutes: 720,
    title: 'The air gets attention throughout the day.',
    copy: 'Carbon dioxide, particles, VOC trends and humidity are monitored. Ventilation responds within the mechanical plan, including the home’s baseline fresh-air requirements.',
  },
  {
    name: 'Dusk', time: '18:30', minutes: 1110,
    title: 'The light changes with the evening.',
    copy: 'Evening scenes lower brightness and bring warmer light. The controls put the lighting designer’s plan into everyday use.',
  },
  {
    name: 'Evening', time: '22:00', minutes: 1320,
    title: 'Selected Wi-Fi access points turn off.',
    copy: 'The wireless schedule follows the household’s priorities. Hardwired connections keep the controls operating while air monitoring and ventilation continue.',
  },
  {
    name: 'Night', time: '02:00', minutes: 1560,
    title: 'Less wireless. The house keeps working.',
    copy: 'The planned night state keeps air monitoring, climate controls and leak sensing active. When someone gets up, a low path light can guide the way.',
  },
];

const supportingPriorities = [
  {
    name: 'Light', label: 'Lighting controls', title: 'Light that follows the day.',
    copy: 'Daytime settings, warm and low evening scenes, and gentle path lighting at night. We coordinate controls with your lighting designer and electrical team.',
  },
  {
    name: 'Water', label: 'Water and leaks', title: 'Notice a leak. Act at the source.',
    copy: 'Leak sensing, automatic shutoff and filtration monitoring, planned with the plumbing team. The design records what happens when a leak is detected.',
  },
  {
    name: 'Quiet', label: 'Everyday operation', title: 'A home that asks less of you.',
    copy: 'Ordinary switches, centralized equipment and fewer competing interfaces. Screens and phone controls are optional; everyday operation works locally.',
  },
];

const questions = [
  [
    'Can you work with our architect or building biologist?',
    'Yes. Bring your team and their recommendations into the Consult. We coordinate the agreed technology priorities with your architect, building biologist and relevant design specialists, translating them into controls, connections and drawings the trades can build.',
  ],
  [
    'Is low RF the same as low EMF?',
    'RF describes radio-frequency signals, including Wi-Fi. A broader low-EMF brief also considers electric and magnetic fields from wiring and equipment. Hardwiring and Wi-Fi schedules alone do not establish the field levels of a finished home; outside sources and personal devices also matter. We define the design priorities and any broader assessment scope with you early.',
  ],
  [
    'Does air monitoring tell us everything about the air?',
    'Sensors measure their specified indicators. CO₂ helps inform ventilation, while particles, VOC trends and humidity add other information. Source control, ventilation and filtration each matter. We coordinate monitoring and controls with the mechanical plan and discuss additional specialist testing separately.',
  ],
  [
    'Does the house need internet, apps or screens?',
    'The controls run locally and work without internet. Ordinary switches remain available. Phone controls and screens are optional; remote care needs an internet connection.',
  ],
  [
    'Are Build and Verify included in the Blueprint?',
    `No. Design, Build and Verify are separately priced. The ${consultPrice} Consult is credited in full toward a separately quoted Blueprint (${blueprintRate.toLowerCase()}). Build is quoted by BAi or your chosen partner. Verify is optional and quoted for the agreed scope.`,
  ],
  [
    'Who selects the hardware and supports the home?',
    'Wellwired selects the hardware in the Blueprint; BAi Systems supplies it, itemized on the quote, for either build route. BAi programs, commissions and supports a BAi build. The partner does that work and provides support on a partner build. Wellwired provides design and its stated construction-question allowance, plus the report if you add Verify.',
  ],
  [
    'What information leaves the home?',
    'The house works locally. Nothing about the people in it leaves unless you turn on remote care. Opting in shares operational data, including history recorded since installation, with your name and address removed. Turning it off stops sharing. If you add Verify, you separately agree to share the handover measurements used in your report.',
  ],
  [
    'Can you help with an existing home?',
    'Wellwired currently focuses on new construction. The controls, wiring and connections are planned together before the walls close. We work remotely with your project team, wherever you build.',
  ],
];

export default function WellwiredNext({ variant = 'a' }) {
  const selectedVariant = variant === 'b' ? 'b' : 'a';
  const opening = openings[selectedVariant];
  const emphasizeWater = selectedVariant === 'b';
  const supportingItems = supportingPriorities.map(item => item.name === 'Water' && emphasizeWater ? {
    ...item,
    id: 'wwn-water',
    label: 'Hardwired water protection',
    title: 'Catch leaks early. Limit the damage.',
    copy: 'We design hardwired leak sensing and automatic water shutoff with your plumbing team. Local controls act on detected leaks to limit further water release, even when selected Wi-Fi is off.',
    detail: 'The Blueprint sets sensor locations, shutoff actions and alerts, alongside filtration monitoring. Detected leaks still need repair, and wet materials need prompt drying.',
  } : item);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scene, setScene] = useState(0);
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const { scrollTo, cancel: cancelScroll } = useWellwiredScroll();
  const activeScene = dayScenes[scene];

  useEffect(() => {
    const update = () => setIsScrolled(window.scrollY > 0);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOutside = event => {
      if (!headerRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [menuOpen]);

  const navigateSection = event => {
    const link = event.target.closest('a[href^="#wwn-"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const hash = link.getAttribute('href');
    const section = document.getElementById(hash.slice(1));
    if (!section) return;
    event.preventDefault();
    setMenuOpen(false);
    const margin = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
    const top = window.scrollY + section.getBoundingClientRect().top - margin;
    if (window.location.hash !== hash) window.history.pushState(window.history.state, '', hash);
    scrollTo(top, {
      onComplete: () => {
        const temporaryTabIndex = !section.hasAttribute('tabindex');
        if (temporaryTabIndex) section.setAttribute('tabindex', '-1');
        section.focus({ preventScroll: true });
        if (temporaryTabIndex) section.addEventListener('blur', () => section.removeAttribute('tabindex'), { once: true });
      },
    });
  };

  return (
    <div className="wellwired wellwired-next" id="wwn-top" data-review-variant={selectedVariant} onClick={navigateSection}>
      <a className="wwn-skip" href="#wwn-main">Skip to content</a>
      <header
        ref={headerRef}
        className={`wwn-header ww-wrap${isScrolled ? ' wwn-header-scrolled' : ''}`}
        onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) setMenuOpen(false);
        }}
        onKeyDown={event => {
          if (event.key === 'Escape' && menuOpen) {
            setMenuOpen(false);
            menuButtonRef.current?.focus();
          }
        }}
      >
        <div className="wwn-header-brand">
          <a className="wwn-lockup" href="#wwn-top" aria-label="Wellwired home" onClick={() => setMenuOpen(false)}>
            <span className="ww-wordmark">Wellwired</span>
          </a>
          <span className="wwn-endorsement">
            <span>by</span>
            <Link to="/" className="wwn-parent-link" aria-label="Back to BAi Systems" title="Back to BAi Systems">
              <img src="/bai-wordmark-white.svg" alt="" width="40" height="17" />
            </Link>
          </span>
        </div>
        <button
          ref={menuButtonRef}
          type="button"
          className="wwn-menu-toggle"
          aria-expanded={menuOpen}
          aria-controls="wwn-navigation"
          onClick={() => setMenuOpen(previous => !previous)}
        >
          <span>{menuOpen ? 'Close' : 'Menu'}</span>
          <span className="ww-plus" aria-hidden="true" />
        </button>
        <nav id="wwn-navigation" className={menuOpen ? 'wwn-navigation-open' : ''} aria-label="Wellwired navigation">
          <a href="#wwn-priorities">The priorities</a>
          <a href="#wwn-design">Design</a>
          <a href="#wwn-build">Build</a>
          <a href="#wwn-verify">Verify</a>
          <a className="wwn-nav-cta" href="#wwn-begin">Let’s talk <span aria-hidden="true">↗</span></a>
        </nav>
      </header>

      <main id="wwn-main">
        <section className="wwn-hero ww-wrap" aria-labelledby="wwn-hero-title">
          <div className="wwn-hero-copy">
            <p className="wwn-kicker">{opening.eyebrow}</p>
            <h1 id="wwn-hero-title">{opening.first}<span>{opening.second}</span></h1>
            <p className="wwn-hero-lede">
              Wellwired is the healthy-home design practice of BAi Systems.
              We design the controls and connections behind your new home’s
              {emphasizeWater ? ' air quality, low-RF connectivity, leak protection and lighting—with' : ' air quality, lighting, water systems and low-RF connectivity—with'}{' '}
              one coordinated plan for your architect and trades.
            </p>
            <a className="wwn-action" href={fitCallUrl || '#wwn-begin'} {...(fitCallUrl ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
              Book a free fit call <span aria-hidden="true">↗</span>
            </a>
            <p className="wwn-hero-meta">New construction. Designed remotely, wherever you build.</p>
          </div>
          <figure className="wwn-hero-visual" aria-label="Architectural concept of an evening bedroom with two warm bedside lamps">
            <RoomStudy moment={4} hero className="wwn-hero-room" />
            <div className="wwn-hero-note">
              <span className="wwn-time">22:00 · Planned night state</span>
              <p>Selected Wi-Fi off.<br />{emphasizeWater ? 'Air and leak monitoring stay on.' : 'Air monitoring stays on.'}</p>
            </div>
            <figcaption className="wwn-caption">Bedroom study · AI concept rendering</figcaption>
          </figure>
          <div className="wwn-hero-bottom">
            <span>All the intelligence. None of the noise.</span>
            <a href="#wwn-priorities">See what goes into the plan <span aria-hidden="true">↓</span></a>
          </div>
        </section>

        <section id="wwn-priorities" className="wwn-priorities ww-wrap wwn-section" aria-labelledby="wwn-priorities-title">
          <h2 id="wwn-priorities-title" className={emphasizeWater ? 'wwn-pillars-heading' : 'sr-only'}>{emphasizeWater ? 'Five pillars: Light, Electrical, Air, Water and Peace.' : 'What we design'}</h2>
          <div className="wwn-priority-grid">
            <article>
              <span className="wwn-index">01 · Air</span>
              <h3>Indoor air quality.</h3>
              <p>Continuous monitoring, with ventilation controls coordinated around measured conditions and the mechanical plan.</p>
              <a className="wwn-text-link" href="#wwn-air">What we design <span aria-hidden="true">↓</span></a>
            </article>
            <article>
              <span className="wwn-index">02 · Electrical</span>
              <h3>Hardwired, <span className="wwn-keep">low-RF</span> technology.</h3>
              <p>Wired controls and network connections first. Wireless use, access-point locations and shutdown schedules considered from the start.</p>
              <a className="wwn-text-link" href="#wwn-signal">How it stays connected <span aria-hidden="true">↓</span></a>
            </article>
            <article>
              <span className="wwn-index">03 · {emphasizeWater ? 'Water' : 'Light'}</span>
              <h3>{emphasizeWater ? 'Leak detection. Automatic shutoff.' : 'Lighting controls that keep time.'}</h3>
              <p>{emphasizeWater ? 'Hardwired sensing and local valve control help limit water damage. Planned to keep working when selected Wi-Fi is off.' : 'Daytime settings, warm and low evening scenes, and gentle nighttime light, coordinated with your lighting designer.'}</p>
              <a className="wwn-text-link" href={emphasizeWater ? '#wwn-water' : '#wwn-support'}>{emphasizeWater ? 'How water protection works' : 'Light, water and peace'} <span aria-hidden="true">↓</span></a>
            </article>
          </div>
        </section>

        <section className="wwn-phase-strip" aria-labelledby="wwn-path-title">
          <div className="ww-wrap">
            <div className="wwn-section-heading">
              <h2 id="wwn-path-title">From design to handover.</h2>
              <p>Each phase is priced separately. Most clients start with design.</p>
            </div>
            <div className="wwn-phase-grid">
              <article>
                <span className="wwn-index">01</span>
                <h3>Design and engineering</h3>
                <p>The Blueprint: selected systems, coordinated drawings and acceptance criteria.</p>
                <a className="wwn-text-link" href="#wwn-design">Start with design <span aria-hidden="true">↗</span></a>
              </article>
              <article>
                <span className="wwn-index">02</span>
                <h3>Build</h3>
                <p>A BAi build or a partner build, using hardware supplied by BAi either way.</p>
                <a className="wwn-text-link" href="#wwn-build">Two build routes <span aria-hidden="true">↗</span></a>
              </article>
              <article>
                <span className="wwn-index">03 · Optional</span>
                <h3>Verify</h3>
                <p>A written handover record of the measurements agreed for your home.</p>
                <a className="wwn-text-link" href="#wwn-verify">The measurement record <span aria-hidden="true">↗</span></a>
              </article>
            </div>
          </div>
        </section>

        <section id="wwn-air" className="wwn-feature wwn-feature-air" aria-labelledby="wwn-air-title">
          <div className="ww-wrap wwn-section wwn-feature-grid">
            <div className="wwn-feature-copy">
              <p className="wwn-kicker">Air · Indoor air quality</p>
              <h2 id="wwn-air-title">Measure the air.<br />Make the home respond.</h2>
              <p className="wwn-lede">Monitoring matters when it informs what the home does next.</p>
              <p>We design continuous monitoring of CO₂, particles, VOC trends and humidity, with controls that coordinate ventilation within your mechanical designer’s plan.</p>
              <ul className="wwn-feature-points">
                <li><strong>Monitor.</strong> Agree the indicators, sensor locations and reporting needs.</li>
                <li><strong>Respond.</strong> Define the ventilation response while maintaining the mechanical plan’s baseline requirements.</li>
                <li><strong>Coordinate.</strong> Put the controls, connections and responsibilities in the Blueprint.</li>
              </ul>
              <p className="wwn-note">Sensors show the conditions they measure. Source control, ventilation and filtration each have a role in the wider air-quality plan.</p>
            </div>
            <figure className="wwn-feature-visual">
              <PillarStudy name="Air" />
              <figcaption className="wwn-caption">Air study · Concept illustration</figcaption>
            </figure>
          </div>
        </section>

        <section id="wwn-signal" className="wwn-feature wwn-feature-signal" aria-labelledby="wwn-signal-title">
          <div className="ww-wrap wwn-section wwn-feature-grid">
            <div className="wwn-feature-copy">
              <p className="wwn-kicker">Electrical · Low-RF connectivity</p>
              <h2 id="wwn-signal-title">Keep the intelligence.<br />Use fewer radios.</h2>
              <p className="wwn-lede">Hardwired controls and network connections form the foundation.</p>
              <p>Where Wi-Fi is wanted, its locations and schedule are part of the design. Hardwired air monitoring and controls keep working when selected access points turn off.</p>
              <p>The home runs locally, with screens and phone controls available when you want them. Everyday operation does not depend on them.</p>
              <div className="wwn-note">
                <h3>Planning a low-EMF home?</h3>
                <p>Low RF addresses wireless transmissions. Broader low-EMF planning also considers electric and magnetic fields from wiring and equipment. Bring your priorities and your building biologist’s recommendations early, so we can define how they shape the technology plan.</p>
              </div>
            </div>
            <figure className="wwn-feature-visual">
              <PillarStudy name="Signal" />
              <figcaption className="wwn-caption">One wired path · Concept illustration</figcaption>
            </figure>
          </div>
        </section>

        <section id="wwn-day" className="wwn-day" data-moment={scene} aria-labelledby="wwn-day-title">
          <div className="ww-wrap wwn-section">
            <div className="wwn-day-head">
              <div className="wwn-section-heading">
                <p className="wwn-kicker">The design in everyday use</p>
                <h2 id="wwn-day-title">One home, through the day.</h2>
              </div>
              <WellwiredClock minutes={activeScene.minutes} />
            </div>
            <p className="wwn-day-cue">Choose a time to explore the home.</p>
            <div className="wwn-day-controls" role="group" aria-label="Choose a time of day" style={{ '--wwn-moment': scene }}>
              {dayScenes.map((item, index) => (
                <button
                  key={item.name}
                  type="button"
                  aria-pressed={scene === index}
                  aria-controls="wwn-day-scene"
                  onClick={() => { cancelScroll(); setScene(index); }}
                >
                  <span className="wwn-time">{item.time}</span>
                  <span>{item.name}</span>
                  <span className="wwn-day-choice-arrow" aria-hidden="true">→</span>
                </button>
              ))}
              <span className="wwn-day-track" aria-hidden="true" />
              <span className="wwn-day-marker" aria-hidden="true"><span className="wwn-day-dot" /></span>
            </div>
            <div className="wwn-day-layout">
              <div id="wwn-day-scene" className="wwn-day-copy" role="region" aria-label="Selected time of day" aria-live="polite" aria-atomic="true">
                <p className="wwn-kicker">{activeScene.name}</p>
                <h3>{activeScene.title}</h3>
                <p>{activeScene.copy}</p>
              </div>
              <figure className="wwn-day-visual" aria-label={`Architectural concept of a bedroom at ${activeScene.time}`}>
                <RoomStudy moment={scene} />
                <figcaption className="wwn-caption">One room. A changing day. <span>AI concept rendering</span></figcaption>
              </figure>
            </div>
            <p className="wwn-caption">Choose a time to explore a planned operating state. The settings are developed for each home.</p>
          </div>
        </section>

        <section id="wwn-support" className="wwn-support ww-wrap wwn-section" aria-labelledby="wwn-support-title">
          <div className="wwn-section-heading">
            <p className="wwn-kicker">Light · Water · Peace</p>
            <h2 id="wwn-support-title">The details belong in the same plan.</h2>
            <p>Air and Electrical are part of a home that also responds to the day, notices leaks and works through familiar controls.</p>
          </div>
          <div className="wwn-support-grid">
            {supportingItems.map(item => (
              <article key={item.name} id={item.id} aria-labelledby={item.id ? `${item.id}-title` : undefined}>
                <figure className="wwn-support-visual">
                  <PillarStudy name={item.name} />
                  <figcaption className="wwn-caption">{item.label} · Concept study</figcaption>
                </figure>
                <h3 id={item.id ? `${item.id}-title` : undefined}>{item.title}</h3>
                <div>
                  <p>{item.copy}</p>
                  {item.detail && <p className="wwn-water-detail">{item.detail}</p>}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="wwn-design" className="wwn-design ww-wrap wwn-section" aria-labelledby="wwn-design-title">
          <div className="wwn-design-intro">
            <div className="wwn-section-heading">
              <p className="wwn-kicker">01 · Design and engineering</p>
              <h2 id="wwn-design-title">First, a direction.<br />Then, a buildable plan.</h2>
              <p>Start with your plans, your household and what matters most. The Consult establishes priorities. The Blueprint makes the selected scope buildable.</p>
              <p>We coordinate the controls and connections with your architect, specialist designers and trades. Already working with a building biologist? Include them in the conversation.</p>
            </div>
            <figure className="wwn-design-photo">
              <img
                src="/images/wellwired/bai-hallway.jpg"
                srcSet="/images/wellwired/bai-hallway-640.jpg 640w, /images/wellwired/bai-hallway.jpg 1365w"
                sizes="(max-width: 680px) 90vw, 42vw"
                width="1365" height="2048" loading="lazy" decoding="async"
                alt="Sunlight crosses the oak floor and paneled walls of a hallway with glass pendant lights."
              />
              <figcaption className="wwn-caption">BAi project photography · Toro Visuals</figcaption>
            </figure>
          </div>
          <div className="wwn-steps">
            <article>
              <span className="wwn-index">01</span>
              <h3>Fit call</h3>
              <h4>Find the fit.</h4>
              <p>A 15-minute conversation about the home, its project stage and whether Wellwired suits the work.</p>
              <strong>Free</strong>
            </article>
            <article>
              <span className="wwn-index">02</span>
              <h3>Consult</h3>
              <h4>A direction, in writing.</h4>
              <p>A virtual working session of about 90 minutes, followed by a written brief: recommendations for air, electrical, light, water and peace, with priorities and a scope and budget range for the Blueprint.</p>
              <strong>{consultPrice}</strong>
              <p className="wwn-price-note">Credited in full toward the Blueprint.</p>
            </article>
            <article>
              <span className="wwn-index">03</span>
              <h3>Blueprint</h3>
              <h4>The package your team builds from.</h4>
              <p>System design, selected hardware and coordinated drawings, plus commissioning requirements and acceptance criteria. A stated allowance covers questions during construction.</p>
              <strong>{blueprintRate}</strong>
              <p className="wwn-price-note">Quoted after your Consult.</p>
            </article>
          </div>
          <div className="wwn-blueprint-detail">
            <h3>Inside the Blueprint</h3>
            <ul>
              <li>Hardware selection and bill of materials.</li>
              <li>Device locations, riser diagrams and panel schedules.</li>
              <li>Trade coordination drawings and labelling.</li>
              <li>Control sequences, commissioning plan and acceptance criteria.</li>
            </ul>
          </div>
        </section>

        <section id="wwn-build" className="wwn-build" aria-labelledby="wwn-build-title">
          <div className="ww-wrap wwn-section">
            <div className="wwn-section-heading">
              <p className="wwn-kicker">02 · Build · Quoted separately</p>
              <h2 id="wwn-build-title">One Blueprint.<br />Two ways to build it.</h2>
              <p>Choose the team that suits your project. BAi Systems supplies the selected hardware for either route, kitted, labelled and itemized on the quote.</p>
            </div>
            <div className="wwn-build-options">
              <article>
                <h3>A BAi build</h3>
                <p>BAi assembles and programs the panels. Local trades install to the drawings; BAi commissions the system and provides ongoing support, including optional remote care.</p>
                <p className="wwn-price-note">BAi quotes the build and support scope.</p>
              </article>
              <article>
                <h3>A partner build</h3>
                <p>A partner who knows the selected system builds the panels, programs and commissions the home, and supports it after handover. They work to the same drawings and acceptance criteria.</p>
                <p className="wwn-price-note">Your partner quotes the build and support scope.</p>
              </article>
            </div>
            <p className="wwn-boundary">Wellwired provides the design and its stated construction-question allowance. Programming, commissioning and ongoing system support belong to the build team you choose.</p>
          </div>
        </section>

        <section id="wwn-verify" className="wwn-verify ww-wrap wwn-section" aria-labelledby="wwn-verify-title">
          <div className="wwn-verify-layout">
            <div>
              <p className="wwn-kicker">03 · Verify · Optional · Quoted separately</p>
              <h2 id="wwn-verify-title">A record of<br />the finished home.</h2>
              <p className="wwn-lede">Add measurements at handover to check agreed design targets.</p>
              <p>Your air-quality and electrical priorities inform the scope. The Verify quote defines what will be measured, where and in which operating states, including day and night comparisons where relevant.</p>
              <p>You receive a written report with the readings, instruments and method recorded, including relevant conditions outside the home’s control.</p>
              <a className="wwn-text-link" href="#wwn-begin">Discuss a Verify scope <span aria-hidden="true">↗</span></a>
            </div>
            <div className="wwn-report" aria-label="Illustrative Verify report structure, with no project measurements">
              <div className="wwn-report-top"><span className="ww-wordmark">Wellwired</span><span>by BAi</span></div>
              <h3>Verify</h3>
              <p className="wwn-report-sub">Project measurement record</p>
              <dl>
                <div><dt>Project</dt><dd>Locations and agreed targets</dd></div>
                <div><dt>Air scope</dt><dd>Indicators defined in the quote</dd></div>
                <div><dt>Electrical scope</dt><dd>Sources and states defined in the quote</dd></div>
                <div><dt>Conditions</dt><dd>Operating state, date and time</dd></div>
                <div><dt>Method</dt><dd>Instruments and procedures</dd></div>
                <div><dt>Findings</dt><dd>Readings and follow-up</dd></div>
              </dl>
              <p className="wwn-report-footer">Illustrative report structure.<br />No project measurements shown.</p>
            </div>
          </div>
        </section>

        <section className="wwn-faq ww-wrap wwn-section" aria-labelledby="wwn-faq-title">
          <div className="wwn-faq-layout">
            <div className="wwn-section-heading">
              <p className="wwn-kicker">Practical questions</p>
              <h2 id="wwn-faq-title">A few things<br />to know early.</h2>
            </div>
            <div>{questions.map(([question, answer]) => (
              <WellwiredDisclosure key={question} title={question}><p>{answer}</p></WellwiredDisclosure>
            ))}</div>
          </div>
        </section>

        <section id="wwn-begin" className="wwn-begin" aria-labelledby="wwn-begin-title">
          <div className="ww-wrap wwn-section wwn-begin-layout">
            <div>
              <p className="wwn-kicker">Begin before the walls close.</p>
              <h2 id="wwn-begin-title">Bring these priorities<br />into the plans.</h2>
              <p>Tell us where you are building, who is on your team and what matters most. We’ll start with a free, 15-minute fit call.</p>
              {fitCallUrl && <a className="wwn-action" href={fitCallUrl} target="_blank" rel="noopener noreferrer">Book a free fit call <span aria-hidden="true">↗</span></a>}
              <p>Architect, designer or building biologist? Choose a design-team conversation in the inquiry form.</p>
              <a className="wwn-text-link" href="mailto:hello@bai-systems.com">hello@bai-systems.com</a>
            </div>
            <WellwiredInquiry includeBuildingBiologist reviewVariant={selectedVariant} />
          </div>
        </section>
      </main>

      <footer className="wwn-footer ww-wrap">
        <div className="wwn-footer-main">
          <div>
            <a className="wwn-lockup" href="#wwn-top"><span className="ww-wordmark">Wellwired</span><span className="wwn-endorsement">by BAi</span></a>
            <p>All the intelligence. None of the noise.</p>
          </div>
          <Link className="wwn-footer-parent" to="/" aria-label="BAi Systems home">
            <img src="/bai-lockup-white.svg" alt="BAi Systems" />
            <span>BAi Systems <span aria-hidden="true">↗</span></span>
          </Link>
        </div>
        <div className="wwn-footer-bottom">
          <span>New construction. Design, Build and optional Verify priced separately.</span>
          <Link to="/wellwired-review">Compare the drafts <span aria-hidden="true">↗</span></Link>
        </div>
      </footer>
    </div>
  );
}
